<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay :show="[STATES.CREATING].some(state.matches)" class="z-20" />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="[STATES.CREATE_ERROR].some(state.matches)"
            :api-response-error="formError"
            class="mb-8"
          />

          <template
            v-if="
              [STATES.LOAD_SUCCESS, STATES.CREATING, STATES.CREATE_ERROR].some(
                state.matches,
              )
            "
          >
            <div v-if="cottageContactDealPage">
              <TText tag="h1" size="lg" variant="semibold" classes="mb-2">
                {{ $t('views.cottage-contact-deal.title') }}
              </TText>
              <TText
                v-if="
                  cottageContactDealPage.owner.answer &&
                  cottageContactDealPage.owner.answer.rate !== null
                "
              >
                {{
                  cottageContactDealPage.owner.answer.rate > 0
                    ? $t('views.cottage-contact-deal.answerRate', {
                        rate: cottageContactDealPage.owner.answer.rate,
                        range: $t(
                          `range.${cottageContactDealPage.owner.answer.range}`,
                        ),
                        value: cottageContactDealPage.owner.answer.value,
                        unit: $t(
                          `time.${cottageContactDealPage.owner.answer.unit}`,
                          cottageContactDealPage.owner.answer.value,
                        ),
                      })
                    : $t('views.cottage-contact-deal.noAnswerRate')
                }}
              </TText>

              <TSeparator class="mt-6 mb-8" />

              <CottageContactDealInfo
                :item="cottageContactDealPage"
              ></CottageContactDealInfo>
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template
            v-if="
              [STATES.LOAD_SUCCESS, STATES.CREATING, STATES.CREATE_ERROR].some(
                state.matches,
              )
            "
          >
            <div v-if="cottageContactDealPage">
              <CottageContactDealResume :item="cottageContactDealPage" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template
            v-if="
              [STATES.LOAD_SUCCESS, STATES.CREATING, STATES.CREATE_ERROR].some(
                state.matches,
              )
            "
          >
            <div v-if="cottageContactDealPage">
              <CottageContactDealForm
                class="mt-4 lg:mt-6"
                :item="cottageContactDealPage"
                @submit="onSubmit"
              />
            </div>
          </template>

          <CottageContactDealPageSuccess
            v-if="
              state.matches(STATES.CREATE_SUCCESS) && cottageContactDealPage
            "
            :id="cottageContactDealPage.id"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// State Machine
import { useMachine } from '@xstate/vue'
import fsm, {
  STATES,
  TRANSITIONS,
} from '../utils/stateMachines/cottageContactDealPageForm.fsm'

// Composables
import { useCottageContactDealPage } from '../composables/useCottageContactDealPage'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import { sendCottageContact } from '~/modules/integrations/hotjar/composables/useHotjarComposable'

// Entities
import type { CottageContactDealPage } from '~/modules/views/cottage-contact-deal/entities/CottageContactDealPage'
import type {
  CottageContactDealPageCreateForm,
  CottageContactDealPageCreateResponse,
} from '../entities/CottageContactDealPageCreate'
import type { CottageContactDealPagePricesDates } from '../entities/CottageContactDealPagePrices'

// Composables
const { sendFormData } = useCottageContactDealPage()
const { setCookieRuralityCottageContact } = useCookiesRurality()
const { state, send } = useMachine(fsm)

// Props
interface propsInterface {
  cottageContactDealPage: CottageContactDealPage
}
const props = defineProps<propsInterface>()

// Constants
let formError: ApiResponseError | null = null

const onSubmit = async (ev: CottageContactDealPageCreateForm) => {
  send(TRANSITIONS.onCreate)
  if (props.cottageContactDealPage) {
    const datesParse: CottageContactDealPagePricesDates = {
      start: new Date(props.cottageContactDealPage.deal.dateIn),
      end: new Date(props.cottageContactDealPage.deal.dateOut),
    }

    const responseSendFormData = await sendFormData({
      createForm: ev,
      cottageContactDealPage: props.cottageContactDealPage,
      dates: datesParse,
      people: props.cottageContactDealPage.deal.people,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      send(TRANSITIONS.onCreateError)
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  cottageContactDealPageCreateResponse: CottageContactDealPageCreateResponse,
  formData: CottageContactDealPageCreateForm,
) => {
  if (props.cottageContactDealPage) {
    setDataRepository(formData)

    sendGa4ContactEvent({
      province: props.cottageContactDealPage.province.name,
      method: 'email',
    })
    sendCottageConversionAll()
    sendCottageContact()
    if (cottageContactDealPageCreateResponse) {
      sendAdsContact({
        email: cottageContactDealPageCreateResponse.traveler.email,
        phone: cottageContactDealPageCreateResponse.traveler.phone,
      })
    }

    send(TRANSITIONS.onCreateSuccess)
  }
}

const setDataRepository = (formData: CottageContactDealPageCreateForm) => {
  if (formData.message.length < 1) {
    formData.message = '...'
  }

  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}
</script>
